/**
 * Middleman's rules the LLM proxy tab applies before sending, in one place.
 *
 * Copied by hand from middleman's admin schemas, so middleman's own test suite
 * (`middleman/tests/test_viewer_admin_rules.py`) parses this file and fails when
 * a copy here drifts from its source. Keep each value a plain string or list
 * of plain strings so that parser can read it. Middleman still validates every
 * write; these only let the form explain a refusal before sending.
 */

/** `LabApi` in middleman's `admin/schemas.py`. */
export const LAB_APIS = [
  'anthropic',
  'openai',
  'openai-legacy-completions',
  'gemini-vertex',
  'gemini-developer',
] as const;
export type LabApi = (typeof LAB_APIS)[number];

/** `_APIS_WITH_BASE_URL`: APIs whose route forwards to the lab's own base URL. */
export const BASE_URL_APIS = [
  'openai',
  'openai-legacy-completions',
  'gemini-vertex',
] as const;

/** `_APIS_WITH_CREDENTIAL`: APIs whose route sends the lab's own credential. */
export const CREDENTIAL_APIS = ['openai', 'openai-legacy-completions'] as const;

/** APIs a lab may list unsupported paths for (`LabConfigInput.check_api_fields`). */
export const UNSUPPORTED_PATHS_APIS = ['openai'] as const;

/** `LabCredentialKind`. */
export const CREDENTIAL_KINDS = ['api_key', 'gcloud_token'] as const;
export type CredentialKind = (typeof CREDENTIAL_KINDS)[number];

/** `middleman.model_info.PROVIDERS`: companies a model or lab can record as serving it. */
export const PROVIDERS = [
  'amazon',
  'anthropic',
  'cerebras',
  'deepinfra',
  'deepseek',
  'fireworks',
  'google',
  'groq',
  'hyperbolic',
  'meta',
  'microsoft',
  'mistral',
  'openai',
  'openrouter',
  'together',
  'xai',
] as const;

/** `ReadModeName` (= `middleman.read_policy.ReadMode`). */
export const READ_MODES = ['provider', 'any', 'tags'] as const;
export type ReadMode = (typeof READ_MODES)[number];

/** `middleman.read_policy.READ_TAG_PATTERN`. */
export const READ_TAG_PATTERN = '^[a-z0-9][a-z0-9-]*$';

/** `LabName`: lab names stay plain identifiers, since model configs reference them. */
export const LAB_NAME_PATTERN = '^[a-z0-9][a-z0-9._-]*$';

/** `ProviderKeyName`: named like the environment variable a key can also come from. */
export const PROVIDER_KEY_NAME_PATTERN = '^[A-Z0-9_]+$';

/** `hawk.core.auth.permissions.PUBLIC_MODEL_GROUP`: Hawk doesn't restrict its transcripts. */
export const PUBLIC_MODEL_GROUP = 'model-access-public';

const has = (list: readonly string[], value: string) => list.includes(value);

/**
 * Whether the viewer knows this lab API. A lab on an API middleman added since
 * keeps all its fields, and middleman decides what's valid, rather than the
 * form dropping ones it doesn't know the API uses.
 */
export const isKnownLabApi = (api: string): api is LabApi => has(LAB_APIS, api);
export const usesBaseUrl = (api: string) =>
  !isKnownLabApi(api) || has(BASE_URL_APIS, api);
export const usesCredential = (api: string) =>
  !isKnownLabApi(api) || has(CREDENTIAL_APIS, api);
export const usesUnsupportedPaths = (api: string) =>
  !isKnownLabApi(api) || has(UNSUPPORTED_PATHS_APIS, api);

/**
 * Whether the form may insist on a field: only for an API it knows needs it.
 * An unknown API shows and keeps every field (above) but requires none, since
 * a lab middleman accepts may have them empty.
 */
export const requiresBaseUrl = (api: string) =>
  isKnownLabApi(api) && has(BASE_URL_APIS, api);
export const requiresCredential = (api: string) =>
  isKnownLabApi(api) && has(CREDENTIAL_APIS, api);

/** An unknown credential kind keeps its key names, as it may send them. */
export const isKnownCredentialKind = (kind: string) =>
  has(CREDENTIAL_KINDS, kind);

/**
 * Select options for one of these lists, plus `current` when it isn't on it: a
 * value middleman accepts that this copy doesn't know yet stays shown and
 * selected, rather than the select silently showing another.
 */
export function optionsWith<T extends string>(
  values: readonly T[],
  current: T | ''
): { value: T; label: string }[] {
  const all =
    current && !values.includes(current) ? [...values, current] : values;
  return all.map(value => ({ value, label: value }));
}
